import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronDownIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { InvoiceItem } from '../../../shared/types/Invoice';
export function InvoiceForm() {
  const {
    t
  } = useTranslation('invoices');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('id');
  const isEditMode = !!invoiceId;
  const [formData, setFormData] = useState({
    invoiceNumber: isEditMode ? 'INV-2024-001' : '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as 'draft' | 'sent'
  });
  const [items, setItems] = useState<InvoiceItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const mockCustomers = [{
    id: '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+60 12 345 6789'
  }, {
    id: '2',
    name: 'Tech Solutions Ltd',
    email: 'accounts@techsolutions.mv',
    phone: '+60 11 234 5678'
  }, {
    id: '3',
    name: 'Global Enterprises',
    email: 'finance@global.com',
    phone: '+60 13 456 7890'
  }, {
    id: '4',
    name: 'Innovation Hub',
    email: 'billing@innovationhub.mv',
    phone: '+60 10 567 8901'
  }];
  const filteredCustomers = mockCustomers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase()));
  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Send to Customer'
  }];
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const calculateItemTotal = (quantity: number, unitPrice: number) => quantity * unitPrice;
  const calculateSubtotal = () => items.reduce((sum, item) => sum + item.total, 0);
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const selectCustomer = (customer: (typeof mockCustomers)[0]) => {
    setFormData({
      ...formData,
      customerName: customer.name,
      customerEmail: customer.email,
      customerPhone: customer.phone
    });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving invoice:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const handleSaveAsDraft = () => {
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white';
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-6878">
      {/* Header */}
      <div className="flex items-center" data-id="element-6879">
        <button onClick={() => navigate('/invoices')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-6880">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-6881" />
          {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-6882">
        {/* Invoice Details Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6883">
          <div className="p-6 border-b border-gray-100" data-id="element-6884">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-6885">
              {t('pages.InvoiceForm.invoice_details')}
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-6886">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-6887">
              <div data-id="element-6888">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6889">
                  {t('pages.InvoiceForm.invoice_number')}
                </label>
                <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({
                ...formData,
                invoiceNumber: e.target.value
              })} placeholder={t('pages.InvoiceForm.attrs.autogenerated_if_left_empty')} className={inputClass} data-id="element-6890" />
              </div>
              <div ref={statusDropdownRef} className="relative" data-id="element-6891">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6892">
                  {t('pages.InvoiceForm.status')}
                </label>
                <button type="button" aria-expanded={showStatusDropdown} aria-haspopup="listbox" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white text-left" data-id="element-6893">
                  <span className="text-gray-900" data-id="element-6894">
                    {statusOptions.find(s => s.value === formData.status)?.label}
                  </span>
                  <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} data-id="element-6895" />
                </button>
                {showStatusDropdown && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1" data-id="element-6896">
                    {statusOptions.map(option => <button key={option.value} type="button" onClick={() => {
                  setFormData({
                    ...formData,
                    status: option.value as 'draft' | 'sent'
                  });
                  setShowStatusDropdown(false);
                }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6897">
                        <span data-id="element-6898">{option.label}</span>
                        {formData.status === option.value && <CheckIcon size={16} className="text-[#9F140F]" data-id="element-6899" />}
                      </button>)}
                  </div>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-6900">
              <div data-id="element-6901">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6902">
                  {t('pages.InvoiceDetail.issue_date')}{' '}
                  <span className="text-red-500" data-id="element-6903">*</span>
                </label>
                <input type="date" required value={formData.issueDate} onChange={e => setFormData({
                ...formData,
                issueDate: e.target.value
              })} className={inputClass} data-id="element-6904" />
              </div>
              <div data-id="element-6905">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6906">
                  {t('pages.InvoiceDetail.due_date')}{' '}
                  <span className="text-red-500" data-id="element-6907">*</span>
                </label>
                <input type="date" required value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className={inputClass} data-id="element-6908" />
              </div>
            </div>

            <div data-id="element-6909">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6910">
                {t('pages.InvoiceDetail.description')}
              </label>
              <input type="text" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder={t('pages.InvoiceForm.attrs.brief_description_of_the_invoice')} className={inputClass} data-id="element-6911" />
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6912">
          <div className="p-6 border-b border-gray-100" data-id="element-6913">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-6914">
              {t('pages.InvoiceForm.customer_information')}
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-6915">
            {/* Searchable Customer Select */}
            <div ref={customerDropdownRef} className="relative" data-id="element-6916">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6917">
                {t('pages.InvoiceForm.customer_name')}{' '}
                <span className="text-red-500" data-id="element-6918">*</span>
              </label>
              <div className="relative" data-id="element-6919">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-6920" />

                <input type="text" required value={showCustomerDropdown ? customerSearch : formData.customerName || customerSearch} onChange={e => {
                setCustomerSearch(e.target.value);
                setFormData({
                  ...formData,
                  customerName: '',
                  customerEmail: '',
                  customerPhone: ''
                });
                setShowCustomerDropdown(true);
              }} onFocus={() => setShowCustomerDropdown(true)} placeholder={t('pages.InvoiceForm.attrs.search_or_enter_customer_name')} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-6921" />
              </div>
              {showCustomerDropdown && filteredCustomers.length > 0 && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto" data-id="element-6922">
                  {filteredCustomers.map(customer => <button key={customer.id} type="button" onClick={() => selectCustomer(customer)} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors" data-id="element-6923">
                      <div data-id="element-6924">
                        <div className="text-sm font-medium text-gray-900" data-id="element-6925">
                          {customer.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-6926">
                          {customer.email}
                        </div>
                      </div>
                      {formData.customerName === customer.name && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-6927" />}
                    </button>)}
                </div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-6928">
              <div data-id="element-6929">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6930">
                  {t('pages.InvoiceForm.email')}{' '}
                  <span className="text-red-500" data-id="element-6931">*</span>
                </label>
                <input type="email" required value={formData.customerEmail} onChange={e => setFormData({
                ...formData,
                customerEmail: e.target.value
              })} placeholder={t('pages.InvoiceForm.attrs.customerexamplecom')} className={inputClass} data-id="element-6932" />
              </div>
              <div data-id="element-6933">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6934">
                  {t('pages.InvoiceForm.phone')}
                </label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({
                ...formData,
                customerPhone: e.target.value
              })} placeholder="+60 12 345 6789" className={inputClass} data-id="element-6935" />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6936">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-6937">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-6938">
              {t('pages.InvoiceForm.invoice_items')}
            </h3>
            <button type="button" onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#9F140F] hover:bg-[#FEE7E5] rounded-lg transition-colors" data-id="element-6939">
              <PlusIcon size={16} data-id="element-6940" />
              {t('pages.InvoiceForm.add_item')}
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-6941">
            {items.map((item, index) => <div key={item.id} className="grid grid-cols-12 gap-3 items-start" data-id="element-6942">
                <div className="col-span-12 md:col-span-5" data-id="element-6943">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6944">
                      {t('pages.InvoiceDetail.description')}
                    </label>}
                  <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder={t('pages.InvoiceForm.attrs.item_description')} className={inputClass} data-id="element-6945" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-6946">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6947">
                      {t('pages.InvoiceDetail.qty')}
                    </label>}
                  <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className={inputClass} data-id="element-6948" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-6949">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6950">
                      {t('pages.InvoiceDetail.unit_price')}
                    </label>}
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className={inputClass} data-id="element-6951" />
                </div>
                <div className="col-span-3 md:col-span-2" data-id="element-6952">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-6953">
                      {t('pages.InvoiceDetail.total')}
                    </label>}
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 font-medium" data-id="element-6954">
                    {t('pages.InvoiceDetail.usd')}{' '}
                    {item.total.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="col-span-1 flex items-end" data-id="element-6955">
                  {index === 0 && <div className="mb-1.5 text-sm" data-id="element-6956">&nbsp;</div>}
                  <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-6957">
                    <TrashIcon size={18} data-id="element-6958" />
                  </button>
                </div>
              </div>)}

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-6959">
              <div className="flex justify-end" data-id="element-6960">
                <div className="w-full max-w-xs space-y-2" data-id="element-6961">
                  <div className="flex justify-between py-1.5" data-id="element-6962">
                    <span className="text-sm text-gray-500" data-id="element-6963">
                      {t('pages.InvoiceDetail.subtotal')}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-6964">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="flex justify-between py-2.5 border-t-2 border-gray-200" data-id="element-6965">
                    <span className="text-base font-semibold text-gray-900" data-id="element-6966">
                      {t('pages.InvoiceDetail.total')}
                    </span>
                    <span className="text-base font-bold text-gray-900" data-id="element-6967">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6968">
          <div className="p-6 border-b border-gray-100" data-id="element-6969">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-6970">
              {t('pages.InvoiceDetail.notes')}
            </h3>
          </div>
          <div className="p-6" data-id="element-6971">
            <textarea value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={3} placeholder={t('pages.InvoiceForm.attrs.additional_notes_or_payment_terms')} className={`${inputClass} resize-none`} data-id="element-6972" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pb-8" data-id="element-6973">
          <button type="button" onClick={() => navigate('/invoices')} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6974">
            {t('pages.InvoiceForm.cancel')}
          </button>
          <button type="button" onClick={handleSaveAsDraft} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6975">
            {t('pages.InvoiceForm.save_as_draft')}
          </button>
          <button type="submit" className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6976">
            {isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>;
}